import { useOutletContext, useParams, useSearchParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useFeatureAvailability } from 'modules/core/hooks/useFeatureAvailability'
import { trackingEvents } from 'modules/core/lib/Tracking'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import ReportsEditor from 'modules/reports/components/Editor'
import NotFound from 'modules/reports/components/NotFound'
import SaveModal, { SAVE_ACTION } from 'modules/reports/components/SaveReportModal'
import useReport from 'modules/reports/hooks/useReport'
import { useReportTracking } from 'modules/reports/hooks/useReportTracking'
import useStore from 'modules/reports/hooks/useStore'
import type { RouteContextParams } from 'modules/reports/lib/RouterContextParams'
import { PromptProvider } from 'modules/reports/lib/promptContext'

const EditReportPage = () => {
  const params = useParams()
  const [search, _] = useSearchParams()
  const { loading } = useOutletContext<RouteContextParams>()
  const { loading: isFeatureLoading, featureAvailability } = useFeatureAvailability(
    Features.analytics_advanced_reports
  )

  const showSaveModal = search.get('action') == SAVE_ACTION

  const { report, isNotFound } = useReport({
    id: String(params.id),
    loading,
  })

  const isAuthorized = featureAvailability.available

  const store = useStore({
    report,
    origin: report?.origin && report.origin == 'template' ? 'modified_template' : undefined,
  })

  const { track } = useReportTracking(store)

  useUnsafeEffect(() => {
    if (!report) return

    track(trackingEvents.reports.reportsEditVisited)
    track(trackingEvents.reports.openCustomerReport)
  }, [track, report])

  return (
    <ResponsivePage.Body
      variant="full"
      authorized={isAuthorized}
      error={undefined}
      loading={loading || isFeatureLoading}
      showHeader={false}
    >
      <NotFound notFound={isNotFound}>
        <PromptProvider>
          <Box flexDirection="column">
            {showSaveModal && report && store && <SaveModal report={report} store={store} />}
            <Box>{store && <ReportsEditor store={store} />}</Box>
          </Box>
        </PromptProvider>
      </NotFound>
    </ResponsivePage.Body>
  )
}

export default EditReportPage
